import { useState, useEffect } from 'react';
import { Clock, Shield, UserX, Zap, Sparkles, Maximize, Minimize, SquareArrowOutUpRight, LayoutGrid, ChevronsUpDown } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useIsMobile } from '@/hooks/use-mobile';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { toast } from '@/hooks/use-toast';
import { Todo, UserProgress, Mission, MissionSize } from '@/types/todo';
import { saveTodos, loadTodos, saveProgress, loadProgress, getResetDay, createDeadline, createDeadlineFromDuration, calculateCompletion, calculateLevel, performDailyReset, trimCompletedTodos } from '@/utils/todoHelpers';
import { createMission, addTodoToMission, updateMissionProgress, saveMissions, loadMissions, completeMission, calculateMissionPoints, trimMissions, ensureGeneralMissionExists, getGeneralMission, createGeneralMission, loadGeneralMissionCount, incrementGeneralMissionCount, resetGeneralMissionCount } from '@/utils/missionHelpers';
import { usePaywallChecker } from '@/hooks/usePaywallChecker';
import { updateSecretTrialCount, hasOverdueTasks, clearPaywallState } from '@/utils/paywallHelpers';
import LevelTracker from '@/components/LevelTracker';
import AddTodoForm from '@/components/AddTodoForm';
import TodoItem from '@/components/TodoItem';
import CompletedTodoItem from '@/components/CompletedTodoItem';
import CompletedMissionItem from '@/components/CompletedMissionItem';
import Settings from '@/components/Settings';
import PaywallModal from '@/components/PaywallModal';
import MissionCards from '@/components/MissionCards';


const Index = () => {
  const [todos, setTodos] = useState<Todo[]>([]);
  const [completedTodos, setCompletedTodos] = useState<Todo[]>([]);
  const [missions, setMissions] = useState<Mission[]>([]);
  const [userProgress, setUserProgress] = useState<UserProgress>({
    totalPoints: 0,
    currentLevel: 1,
    lastResetDate: getResetDay()
  });
  const [activeTab, setActiveTab] = useState('active');
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isResetAnimating, setIsResetAnimating] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsDefaultSection, setSettingsDefaultSection] = useState<string | undefined>(undefined);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hasShownMainElements, setHasShownMainElements] = useState(false);
  const [hasShownActionElements, setHasShownActionElements] = useState(false);
  const [showToggleGroup, setShowToggleGroup] = useState(() => {
    return localStorage.getItem('hideToggleGroup') !== 'true';
  });
  const [showTopCards, setShowTopCards] = useState(() => {
    return localStorage.getItem('hideTopCards') !== 'true';
  });
  const [layoutMode, setLayoutMode] = useState(() => {
    return localStorage.getItem('layoutMode') || 'missions';
  });
  const [generalMissionCount, setGeneralMissionCount] = useState(0);
  const showMissions = layoutMode === 'missions' || layoutMode === 'missions-full';
  const isMobile = useIsMobile();

  // Detect if this is a popup window
  const isPopupMode = new URLSearchParams(window.location.search).has('view') && 
                      new URLSearchParams(window.location.search).get('view') === 'popup';

  // Store popup window reference and status
  const [popupWindow, setPopupWindow] = useState<Window | null>(null);
  const [isPopupActive, setIsPopupActive] = useState(() => {
    return localStorage.getItem('popupActive') === 'true';
  });

  // Paywall functionality - combine active and completed todos for accurate counting
  const allTodos = [...todos, ...completedTodos];
  const {
    isPaywallActive
  } = usePaywallChecker(allTodos);

  // Load data on mount
  useEffect(() => {
    const loadedTodos = loadTodos('dailyTodos');
    const loadedCompleted = loadTodos('completedTodos');
    const loadedProgress = loadProgress();
    let loadedMissions = loadMissions();
    const loadedGeneralCount = loadGeneralMissionCount();
    
    // Ensure General mission exists
    const missionsWithGeneral = ensureGeneralMissionExists(loadedMissions);
    if (missionsWithGeneral.length !== loadedMissions.length) {
      loadedMissions = missionsWithGeneral;
      saveMissions(loadedMissions);
    }
    
    setTodos(loadedTodos);
    setCompletedTodos(loadedCompleted);
    setUserProgress(loadedProgress);
    setMissions(loadedMissions);
    setGeneralMissionCount(loadedGeneralCount);

    // Check for daily reset
    checkDailyReset(loadedTodos, loadedProgress);
    
    // Mark data as loaded
    setDataLoaded(true);
  }, []);


  // Enhanced client-side reset: Check every minute for automatic daily reset
  useEffect(() => {
    const interval = setInterval(() => {
      const currentProgress = loadProgress();
      const today = getResetDay();
      
      // Only perform reset if needed and data is loaded
      if (dataLoaded && currentProgress.lastResetDate !== today) {
        const currentTodos = loadTodos('dailyTodos');
        checkDailyReset(currentTodos, currentProgress);
      }
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [dataLoaded]);

  // Check if daily reset is needed
  const checkDailyReset = (currentTodos: Todo[], currentProgress: UserProgress) => {
    const today = getResetDay();
    if (currentProgress.lastResetDate !== today && !isResetAnimating) {
      setIsResetAnimating(true);
      
      // Perform daily reset (includes paywall state reset)
      const {
        todos: updatedTodos,
        userProgress: newProgress,
        conversionCount
      } = performDailyReset(currentTodos, currentProgress);
      
      // Wait for reset animation to complete before updating state
      setTimeout(() => {
        setTodos(updatedTodos);
        setUserProgress(newProgress);
        resetGeneralMissionCount();
        setGeneralMissionCount(0);

        // Save updates
        saveTodos('dailyTodos', updatedTodos);
        saveProgress(newProgress);
        setIsResetAnimating(false);

        // Show overdrive notification if any tasks were converted
        if (conversionCount > 0) {
          toast({
            title: `🚀 ${conversionCount} task${conversionCount === 1 ? '' : 's'} entered Overdrive!`,
            description: "Complete them today for 3x points"
          });
        }
      }, 100); // Small delay to ensure animation triggers
    }
  };

  // Open settings modal from external components
  const openSettingsModal = (defaultSection?: string) => {
    setSettingsDefaultSection(defaultSection);
    setShowSettingsModal(true);
  };

  // Add new todo
  const addTodo = (title: string, durationMinutes?: number, missionId?: string) => {
    const newTodo: Todo = {
      id: Date.now().toString(),
      title,
      createdAt: new Date(),
      deadline: durationMinutes ? createDeadlineFromDuration(durationMinutes) : createDeadline(),
      completed: false,
      missionId
    };
    const updatedTodos = [newTodo, ...todos];
    setTodos(updatedTodos);
    saveTodos('dailyTodos', updatedTodos);

    // Update mission progress if task was added to a mission
    if (missionId) {
      const allUpdatedTodos = [...updatedTodos, ...completedTodos];
      setMissions(currentMissions => {
        const updatedMissions = trimMissions(updateMissionProgress(currentMissions, allUpdatedTodos));
        saveMissions(updatedMissions);
        return updatedMissions;
      });
    }
  };

  // Complete todo
  const completeTodo = (id: string) => {
    const todo = todos.find(t => t.id === id);
    if (!todo) return;
    const completion = calculateCompletion(todo.createdAt, todo.deadline, todo.isOverdrive || false);
    const completedTodo: Todo = {
      ...todo,
      completed: true,
      completedAt: new Date(),
      pointsEarned: completion.points
    };
    const updatedTodos = todos.filter(t => t.id !== id);
    const updatedCompleted = trimCompletedTodos([completedTodo, ...completedTodos]);

    // Update user progress
    const newTotalPoints = userProgress.totalPoints + completion.points;
    const {
      level
    } = calculateLevel(newTotalPoints);
    const updatedProgress: UserProgress = {
      ...userProgress,
      totalPoints: newTotalPoints,
      currentLevel: level
    };
    setTodos(updatedTodos);
    setCompletedTodos(updatedCompleted);
    setUserProgress(updatedProgress);

    // Save updates
    saveTodos('dailyTodos', updatedTodos);
    saveTodos('completedTodos', updatedCompleted);
    saveProgress(updatedProgress);
    
    // Update mission progress
    const allUpdatedTodos = [...updatedTodos, ...updatedCompleted];
    setMissions(currentMissions => {
      const updatedMissions = trimMissions(updateMissionProgress(currentMissions, allUpdatedTodos));
      saveMissions(updatedMissions);
      return updatedMissions;
    });
    
    // Update secret trial counter (persists through data resets)
    updateSecretTrialCount();
    
    // Check if all overdue tasks are now completed and clear paywall if so
    if (!hasOverdueTasks(updatedTodos)) {
      clearPaywallState();
    }
  };

  // Edit todo
  const editTodo = (id: string, newTitle: string) => {
    const updatedTodos = todos.map(todo => todo.id === id ? {
      ...todo,
      title: todo.isOverdrive ? `🚀 ${newTitle}` : newTitle
    } : todo);
    setTodos(updatedTodos);
    saveTodos('dailyTodos', updatedTodos);
  };

  // Boost todo (reduce time remaining)
  const boostTodo = (id: string, percentage: number) => {
    const updatedTodos = todos.map(todo => {
      if (todo.id === id) {
        const now = new Date();
        const timeRemaining = todo.deadline.getTime() - now.getTime();
        const reducedTime = timeRemaining * (percentage / 100);
        const newDeadline = new Date(todo.deadline.getTime() - reducedTime);
        
        return {
          ...todo,
          deadline: newDeadline
        };
      }
      return todo;
    });
    
    setTodos(updatedTodos);
    saveTodos('dailyTodos', updatedTodos);
  };

  // Delete todo
  const deleteTodo = (id: string) => {
    const updatedTodos = todos.filter(t => t.id !== id);
    setTodos(updatedTodos);
    saveTodos('dailyTodos', updatedTodos);
  };

  // Mission management functions
  const handleAddToMission = (todoId: string, missionId: string, mission?: Mission) => {
    const updatedTodos = addTodoToMission(todos, todoId, missionId);
    const updatedCompleted = trimCompletedTodos(addTodoToMission(completedTodos, todoId, missionId));
    
    setTodos(updatedTodos);
    setCompletedTodos(updatedCompleted);
    saveTodos('dailyTodos', updatedTodos);
    saveTodos('completedTodos', updatedCompleted);
    
    // Update mission progress
    const allUpdatedTodos = [...updatedTodos, ...updatedCompleted];
    setMissions(currentMissions => {
      const updatedMissions = trimMissions(updateMissionProgress(currentMissions, allUpdatedTodos));
      saveMissions(updatedMissions);
      return updatedMissions;
    });

    const missionName = mission?.name || missions.find(m => m.id === missionId)?.name;
  };

  const handleRemoveFromMission = (todoId: string) => {
    const updatedTodos = todos.map(todo => 
      todo.id === todoId ? { ...todo, missionId: undefined } : todo
    );
    const updatedCompleted = trimCompletedTodos(completedTodos.map(todo => 
      todo.id === todoId ? { ...todo, missionId: undefined } : todo
    ));
    
    setTodos(updatedTodos);
    setCompletedTodos(updatedCompleted);
    saveTodos('dailyTodos', updatedTodos);
    saveTodos('completedTodos', updatedCompleted);
    
    // Update mission progress
    const allUpdatedTodos = [...updatedTodos, ...updatedCompleted];
    setMissions(currentMissions => {
      const updatedMissions = trimMissions(updateMissionProgress(currentMissions, allUpdatedTodos));
      saveMissions(updatedMissions);
      return updatedMissions;
    });

  };

  const handleCreateMission = (name: string, size: MissionSize): { id: string; mission: Mission } => {
    const newMission = createMission(name, size);
    setMissions(currentMissions => {
      const updatedMissions = trimMissions([newMission, ...currentMissions]);
      saveMissions(updatedMissions);
      return updatedMissions;
    });
    
    
    return { id: newMission.id, mission: newMission };
  };

  // Complete mission
  const handleCompleteMission = (missionId: string) => {
    const completedMission = missions.find(m => m.id === missionId);
    const missionName = completedMission?.name;
    // Calculate bonus points based on completed tasks only
    const completedTasks = allTodos.filter(todo => todo.missionId === missionId && todo.completed);
    const bonusPoints = completedTasks.length * 10;
    
    // Award bonus points for mission completion
    setUserProgress(currentProgress => {
      const newTotalPoints = currentProgress.totalPoints + bonusPoints;
      const newLevel = calculateLevel(newTotalPoints);
      const updatedProgress = {
        ...currentProgress,
        totalPoints: newTotalPoints,
        currentLevel: newLevel.level
      };
      saveProgress(updatedProgress);
      return updatedProgress;
    });
    
    setMissions(currentMissions => {
      let updatedMissions = completeMission(currentMissions, missionId);
      
      // If this was the General mission, create a new one and increment counter
      if (completedMission?.isGeneral) {
        const count = incrementGeneralMissionCount();
        setGeneralMissionCount(count + 1);
        updatedMissions = [createGeneralMission(), ...updatedMissions];
      }
      
      updatedMissions = trimMissions(updatedMissions);
      saveMissions(updatedMissions);
      return updatedMissions;
    });

  };

  // Edit mission
  const handleEditMission = (missionId: string, newName: string) => {
    setMissions(currentMissions => {
      const updatedMissions = trimMissions(currentMissions.map(mission => 
        mission.id === missionId ? { ...mission, name: newName } : mission
      ));
      saveMissions(updatedMissions);
      return updatedMissions;
    });

  };

  // Delete mission
  const handleDeleteMission = (missionId: string) => {
    // Remove mission from all todos
    const updatedTodos = todos.map(todo => 
      todo.missionId === missionId ? { ...todo, missionId: undefined } : todo
    );
    const updatedCompleted = trimCompletedTodos(completedTodos.map(todo => 
      todo.missionId === missionId ? { ...todo, missionId: undefined } : todo
    ));

    setTodos(updatedTodos);
    setCompletedTodos(updatedCompleted);
    saveTodos('dailyTodos', updatedTodos);
    saveTodos('completedTodos', updatedCompleted);

    // Remove mission from missions list
    setMissions(currentMissions => {
      const updatedMissions = trimMissions(currentMissions.filter(mission => mission.id !== missionId));
      saveMissions(updatedMissions);
      return updatedMissions;
    });

    const missionName = missions.find(m => m.id === missionId)?.name;
  };

  // Reset all data
  const resetAllData = () => {
    if (isResetAnimating) return;
    
    setIsResetAnimating(true);
    
    // Wait for reset animation to complete before clearing data
    setTimeout(() => {
      setTodos([]);
      setCompletedTodos([]);
      setUserProgress({
        totalPoints: 0,
        currentLevel: 1,
        lastResetDate: getResetDay()
      });
      
      // Clear todo data
      localStorage.removeItem('dailyTodos');
      localStorage.removeItem('completedTodos');
      localStorage.removeItem('userProgress');
      localStorage.removeItem('missions');
      resetGeneralMissionCount();
      setGeneralMissionCount(0);
      
      // Clear appearance settings
      localStorage.removeItem('hideToggleGroup');
      localStorage.removeItem('hideTopCards');
      localStorage.removeItem('showMissions');
      
      // Clear time settings
      localStorage.removeItem('timeSettings');
      
      // Clear UI state
      localStorage.removeItem('level2ModalShown');
      localStorage.removeItem('popupActive');
      
      // Reset visibility states to defaults
      setShowToggleGroup(true);
      setShowTopCards(true);
      setLayoutMode('standard');
      setMissions([]);
      
      setIsResetAnimating(false);
      
      toast({
        title: "All data reset",
        description: "Your todos, progress, and settings have been cleared"
      });
    }, 100); // Small delay to ensure animation triggers
  };

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (error) {
      console.error('Error toggling fullscreen:', error);
    }
  };

  const openPopupWindow = () => {
    // Check if popup already exists and is open
    if (popupWindow && !popupWindow.closed) {
      popupWindow.focus();
      return;
    }

    const width = 420;
    const height = 700;
    const left = window.screen.width - width - 50; // Position on right side with margin
    const top = 50;

    const popup = window.open(
      `${window.location.origin}/?view=popup`,
      'countdown-todo-popup',
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes,status=no,location=no,toolbar=no`
    );

    if (popup) {
      setPopupWindow(popup);
      setIsPopupActive(true);
      localStorage.setItem('popupActive', 'true');
      
      toast({
        title: "App opened in popup",
        description: "Main window minimized. Use popup or return to main window."
      });
    } else {
      toast({
        title: "Popup blocked",
        description: "Please allow popups for this site and try again"
      });
    }
  };

  const returnToMainWindow = () => {
    if (popupWindow && !popupWindow.closed) {
      popupWindow.close();
    }
    setIsPopupActive(false);
    setPopupWindow(null);
    localStorage.removeItem('popupActive');
    
    toast({
      title: "Returned to main window",
      description: "App is now running in the main window"
    });
  };

  const toggleMissions = () => {
    let newLayoutMode: string;
    
    if (layoutMode === 'standard') {
      newLayoutMode = 'missions';
    } else if (layoutMode === 'missions') {
      newLayoutMode = 'missions-full';
    } else {
      newLayoutMode = 'standard';
    }
    
    setLayoutMode(newLayoutMode);
    localStorage.setItem('layoutMode', newLayoutMode);
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Monitor popup window status
  useEffect(() => {
    if (!popupWindow || isPopupMode) return;

    const interval = setInterval(() => {
      if (popupWindow.closed) {
        setIsPopupActive(false);
        setPopupWindow(null);
        localStorage.removeItem('popupActive');
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [popupWindow, isPopupMode]);

  // Track when elements should animate in for the first time
  useEffect(() => {
    if (allTodos.length > 0 && !hasShownMainElements) {
      setHasShownMainElements(true);
    }
    if (completedTodos.length > 0 && !hasShownActionElements) {
      setHasShownActionElements(true);
    }
  }, [allTodos.length, completedTodos.length, hasShownMainElements, hasShownActionElements]);

  // Listen for storage events to sync data between windows
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'dailyTodos') {
        const updatedTodos = loadTodos('dailyTodos');
        setTodos(updatedTodos);
      } else if (e.key === 'completedTodos') {
        const updatedCompleted = loadTodos('completedTodos');
        setCompletedTodos(updatedCompleted);
      } else if (e.key === 'userProgress') {
        const updatedProgress = e.newValue ? JSON.parse(e.newValue) : {
          totalPoints: 0,
          currentLevel: 1,
          lastResetDate: getResetDay()
        };
        setUserProgress(updatedProgress);
      } else if (e.key === 'missions') {
        const updatedMissions = loadMissions();
        setMissions(updatedMissions);
      } else if (e.key === 'popupActive') {
        setIsPopupActive(e.newValue === 'true');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const activeTodos = todos.filter(t => !t.completed);
  const completedCount = completedTodos.length;

  // If popup is active and this is not the popup window, show minimized UI
  if (isPopupActive && !isPopupMode) {
    return (
      <div className="min-h-[100svh] py-4 px-4 bg-background overflow-x-hidden">
        <div className="max-w-xl mx-auto">
          <div className="text-center py-20">
            <div className="mb-8">
              <h1 className="text-3xl font-bold mb-2 text-foreground">App Running in Popup</h1>
              <p className="text-muted-foreground mb-8">
                Countdown todo is currently running in a popup window for better focus and productivity.
              </p>
            </div>
            
            <Button 
              onClick={returnToMainWindow}
              size="lg"
              className="mb-4"
            >
              Use this Window
            </Button>
            
            <p className="text-xs text-muted-foreground">
              Or close the popup window to automatically return here
            </p>
          </div>
        </div>
      </div>
    );
  }

  return <div style={{
    touchAction: 'manipulation',
    WebkitUserSelect: 'none',
    WebkitTouchCallout: 'none'
  }} className="min-h-[100svh] py-4 px-4 bg-background overflow-x-hidden">
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-center mb-3 relative">
          <div className="absolute left-0 flex items-center gap-1">
            {!isMobile && completedTodos.length > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={toggleFullscreen}
                    className={`h-8 w-8 ${hasShownActionElements && completedTodos.length > 0 && completedTodos.length === 1 ? 'animate-fade-in' : ''}`}
                    aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                  >
                    {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                </TooltipContent>
              </Tooltip>
            )}
            {!isMobile && !isPopupMode && completedTodos.length > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={openPopupWindow}
                    className={`h-8 w-8 ${hasShownActionElements && completedTodos.length > 0 && completedTodos.length === 1 ? 'animate-fade-in' : ''}`}
                    aria-label="Open in popup window"
                  >
                    <SquareArrowOutUpRight className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  Open in popup window
                </TooltipContent>
              </Tooltip>
            )}
            {completedTodos.length > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={toggleMissions}
                    className={`h-8 w-8 ${hasShownActionElements && completedTodos.length > 0 && completedTodos.length === 1 ? 'animate-fade-in' : ''}`}
                    aria-label="Cycle layout mode"
                  >
                    <LayoutGrid className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  Cycle layout mode
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          <ToggleGroup 
            type="single" 
            value={activeTab} 
            onValueChange={value => value && setActiveTab(value)} 
            className={`rounded-md p-0.5 bg-muted w-64 h-8 ${allTodos.length > 0 ? 'opacity-100 visible' : 'opacity-0 invisible'} ${!showToggleGroup ? 'opacity-0 pointer-events-none' : ''}`}
          >
            <ToggleGroupItem value="active" className="rounded-sm px-2 py-0 text-xs flex-1 h-7 data-[state=on]:bg-card data-[state=on]:text-foreground data-[state=on]:shadow-[inset_0_0_0_1px] data-[state=on]:shadow-border">
              Active Tasks ({activeTodos.length})
            </ToggleGroupItem>
            <ToggleGroupItem value="completed" className="rounded-sm px-2 py-0 text-xs flex-1 h-7 data-[state=on]:bg-card data-[state=on]:text-foreground data-[state=on]:shadow-[inset_0_0_0_1px] data-[state=on]:shadow-border">
              Completed ({completedCount})
            </ToggleGroupItem>
          </ToggleGroup>
          <div className="absolute right-0 flex items-center gap-1">
            {completedTodos.length > 0 && (
              <div className={hasShownActionElements && completedTodos.length === 1 ? 'animate-fade-in' : ''}>
                <Settings 
                  onResetData={resetAllData} 
                  todos={todos}
                  onToggleGroupVisibilityChange={setShowToggleGroup}
                  onLevelTrackerVisibilityChange={setShowTopCards}
                  onMissionsVisibilityChange={setLayoutMode}
                />
              </div>
            )}
          </div>
        </div>

        {/* Level Tracker and Mission Cards */}
        {dataLoaded && activeTab === 'active' && showTopCards && (
          <div className={allTodos.length === 0 ? 'opacity-0 pointer-events-none' : 'opacity-100'}>
            {layoutMode === 'missions-full' ? (
              <div className="mb-6">
                <MissionCards
                  missions={missions}
                  allTodos={allTodos}
                  activeTab={activeTab}
                  isMobile={isMobile}
                  generalMissionCount={generalMissionCount}
                  onCompleteMission={handleCompleteMission}
                  onEditMission={handleEditMission}
                  onDeleteMission={handleDeleteMission}
                  onCreateMission={handleCreateMission}
                />
              </div>
            ) : showMissions ? (
              <div className="grid grid-cols-2 gap-4 mb-6 items-stretch">
                <div className="h-full">
                  <LevelTracker
                    totalPoints={userProgress.totalPoints}
                    isInitialAppearance={false}
                    isMobile={isMobile}
                    showMissions={showMissions}
                    onLevelUp={(newLevel) => {
                       // Add auto-todo when user reaches level 2 (if not already shown)
                       if (newLevel === 2 && !localStorage.getItem('level2ModalShown')) {
                         // Delay adding the todo until after the level up animation completes
                       setTimeout(() => {
                          const generalMission = getGeneralMission(missions);
                          addTodo("Add your own task", 10, generalMission?.id);
                       }, 2000); // 2 seconds to enjoy level up celebration
                         localStorage.setItem('level2ModalShown', 'true');
                       }
                     }}
                     onResetAnimation={() => {
                      // Animation completed, this will be called after countdown finishes
                    }}
                  />
                </div>
                <div className="h-full">
                  <MissionCards
                    missions={missions}
                    allTodos={allTodos}
                    activeTab={activeTab}
                    isMobile={isMobile}
                    generalMissionCount={generalMissionCount}
                    onCompleteMission={handleCompleteMission}
                    onEditMission={handleEditMission}
                    onDeleteMission={handleDeleteMission}
                    onCreateMission={handleCreateMission}
                  />
                </div>
              </div>
            ) : (
              <div className="mb-6">
                <LevelTracker
                  totalPoints={userProgress.totalPoints}
                  isInitialAppearance={false}
                  onLevelUp={(newLevel) => {
                    // Add auto-todo when user reaches level 2 (if not already shown)
                    if (newLevel === 2 && !localStorage.getItem('level2ModalShown')) {
                      // Delay adding the todo until after the level up animation completes
                      setTimeout(() => {
                        const generalMission = getGeneralMission(missions);
                        addTodo("Add your own task", 10, generalMission?.id);
                      }, 2000); // 2 seconds to enjoy level up celebration
                      localStorage.setItem('level2ModalShown', 'true');
                    }
                  }}
                  onResetAnimation={() => {
                    // Animation completed, this will be called after countdown finishes
                  }}
                />
              </div>
            )}
          </div>
        )}


        <div className={isPaywallActive ? 'pointer-events-none opacity-50' : ''}>
          {/* Add Todo Form */}
          {activeTab === 'active' && (
            <AddTodoForm 
              onAdd={addTodo} 
              onOpenSettings={openSettingsModal}
              missions={missions}
              showMissions={showMissions}
              onCreateMission={handleCreateMission}
            />
          )}

          {/* Mission Cards */}

          {/* Content */}
          <div className="space-y-4 mt-6">
          {activeTab === 'active' ? activeTodos.length === 0 ? <div className="text-center py-12 text-muted-foreground">
                <p className="text-lg mb-2 text-foreground">{completedTodos.length === 0 ? 'Get things done' : 'No active todos'}</p>
                {completedTodos.length === 0 ? (
                  <div className="space-y-6">
                    <div className="text-sm space-y-2 max-w-fit mx-auto">
                      <p className="flex items-center justify-start gap-2">
                        <Clock size={16} className="text-muted-foreground" />
                        Countdown timers on every task
                      </p>
                      <p className="flex items-center justify-start gap-2">
                        <Shield size={16} className="text-muted-foreground" />
                        No cloud. Your data is private
                      </p>
                      <p className="flex items-center justify-start gap-2">
                        <Zap size={16} className="text-muted-foreground" />
                        No signup. No ads. No subscription
                      </p>
                    </div>
                    <div className="animated-button mx-auto block w-fit">
                      <Button 
                        onClick={() => {
                          const generalMission = getGeneralMission(missions);
                          addTodo("Press the complete button", 3, generalMission?.id);
                        }}
                        size="lg"
                        className="flex items-center gap-2"
                      >
                        Try now
                        <Sparkles size={16} />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm">Add a new todo to get started!</p>
                )}
              </div> : <div className="space-y-3">
                {activeTodos.map(todo => <TodoItem 
                  key={todo.id} 
                  todo={todo} 
                  onComplete={completeTodo} 
                  onEdit={editTodo} 
                  onDelete={deleteTodo} 
                  onBoost={boostTodo}
                  missions={missions}
                  showMissions={showMissions}
                  onAddToMission={handleAddToMission}
                  onCreateMission={handleCreateMission}
                  onRemoveFromMission={handleRemoveFromMission}
                />)}
              </div> : (
                <div className="space-y-3">
                  {/* Completed missions - collapsible section */}
                  {missions.filter(m => m.isCompleted).length > 0 && (
                    <Collapsible defaultOpen={false}>
                      <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
                        <span className="text-sm font-medium text-muted-foreground">
                          Completed Missions ({missions.filter(m => m.isCompleted).length})
                        </span>
                        <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="space-y-3 mt-3">
                        {missions.filter(m => m.isCompleted).map(mission => (
                          <CompletedMissionItem key={mission.id} mission={mission} allTodos={allTodos} />
                        ))}
                      </CollapsibleContent>
                    </Collapsible>
                  )}
                  
                  {/* Completed todos */}
                  {completedTodos.length === 0 && missions.filter(m => m.isCompleted).length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <p className="text-lg mb-2">No completed todos</p>
                      <p className="text-sm">Complete some tasks to see them here!</p>
                    </div>
                  ) : (
                    completedTodos.map(todo => <CompletedTodoItem key={todo.id} todo={todo} />)
                  )}
                </div>
              )}
          </div>
        </div>
        
        {/* Paywall Modal */}
        <PaywallModal isOpen={isPaywallActive} todos={todos} completedTodos={completedTodos} onClose={() => {}} />
        
        
        {/* Settings Modal */}
        {showSettingsModal && (
          <Settings 
            todos={todos}
            defaultOpen={true}
            defaultSection={settingsDefaultSection}
            onResetData={resetAllData}
            onToggleGroupVisibilityChange={setShowToggleGroup}
            onLevelTrackerVisibilityChange={setShowTopCards}
            onMissionsVisibilityChange={setLayoutMode}
            onClose={() => {
              setShowSettingsModal(false);
              setSettingsDefaultSection(undefined);
            }}
          />
        )}
        
      </div>
      
      {/* Fixed Footer - Hidden for now */}
      {/* <footer className="fixed bottom-0 left-0 right-0 py-3 px-4 bg-background/80 backdrop-blur-sm text-center text-xs text-muted-foreground">
        <div className="flex justify-center items-center gap-4">
          <a 
            href="https://countdowntodo.com" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="underline hover:text-foreground transition-colors"
          >
            Countdown Todo
          </a>
          <span>•</span>
          <a 
            href="https://countdowntodo.com/privacy" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="underline hover:text-foreground transition-colors"
          >
            Privacy Policy
          </a>
        </div>
      </footer> */}
    </div>;
};
export default Index;